/**
 * "This write took the workspace over its storage limit" signal.
 *
 * Any BE response whose write pushed the tenant past its storage quota
 * carries a top-level `meta: { storageWentOverLimit: true }` beside
 * `data` — upload presigns on every surface, wiki page duplicate, wiki
 * paste adopt, and the card / comment / wiki-comment saves that copy
 * pasted files. Only the request that actually crosses the line carries
 * it, so the person who went over learns it at the moment it happens
 * rather than the next morning, when the nightly reaper starts the grace
 * window.
 *
 * `apiClient` inspects every successful JSON body and calls
 * {@link notifyStorageWentOverLimit}; one app-level listener (mounted in
 * the authenticated shell) turns that into a toast. Keeping the detection
 * in the transport means no individual upload hook has to remember it,
 * and callers keep receiving exactly the body they always did.
 *
 * Deduplicated: a multi-file drop fires one presign per file, and the
 * first file that crosses the line leaves the tenant over — but a burst
 * of concurrent presigns can still see several responses carry the flag.
 * Repeats inside {@link DEDUPE_WINDOW_MS} are swallowed so the user reads
 * one message, not one per file.
 */

export const DEDUPE_WINDOW_MS = 10_000

type Listener = () => void

const listeners = new Set<Listener>()
let lastFiredAt: number | null = null
/**
 * A signal raised while nothing was listening (the shell not mounted yet,
 * or between a remount's cleanup and resubscribe). Held — one is enough,
 * they all say the same thing — and delivered to the next subscriber.
 */
let pending = false

/** True when a parsed response body carries the over-limit marker. */
export function hasStorageWentOverLimit(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const meta = (body as { meta?: unknown }).meta
  if (typeof meta !== 'object' || meta === null) return false
  return (meta as { storageWentOverLimit?: unknown }).storageWentOverLimit === true
}

/**
 * Register the app-level handler. Returns an unsubscribe function, so a
 * React effect can hand it straight back as its cleanup.
 */
export function subscribeStorageWentOverLimit(listener: Listener): () => void {
  listeners.add(listener)
  if (pending) {
    pending = false
    lastFiredAt = Date.now()
    deliver(listener)
  }
  return () => {
    listeners.delete(listener)
  }
}

/**
 * A listener that throws is contained: this runs on the success path of a
 * request whose write already landed, and a broken toast must never turn
 * that into a rejected promise for the caller.
 */
function deliver(listener: Listener): void {
  try {
    listener()
  } catch {
    // Swallowed deliberately — see the doc comment.
  }
}

/**
 * Notify listeners, at most once per {@link DEDUPE_WINDOW_MS}. The window
 * only starts when a listener actually receives the signal; with nobody
 * subscribed it is held for the next subscriber instead of being consumed.
 */
export function notifyStorageWentOverLimit(now: number = Date.now()): void {
  if (lastFiredAt !== null && now - lastFiredAt < DEDUPE_WINDOW_MS) return
  if (listeners.size === 0) {
    pending = true
    return
  }
  lastFiredAt = now
  for (const listener of listeners) deliver(listener)
}

/** Test-only: forget the dedupe clock and every registered listener. */
export function resetStorageWentOverLimitSignal(): void {
  listeners.clear()
  lastFiredAt = null
  pending = false
}
